import { type FormEvent, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { AuthFrame, Button, ErrorNote, Field, Icon, TextInput } from '../components/ui/index.js';
import { useAdminT } from '../lib/admin-locale.js';
import { useSession } from '../lib/session.js';
import { useUiPrefs } from '../lib/ui-prefs.js';

/** Short login blurb — full marketing copy stays on the brand panel. */
function shortLoginSubtitle(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const sentence = trimmed.split(/(?<=[.!?])\s+/)[0] ?? trimmed;
  return sentence.length > 140 ? `${sentence.slice(0, 137).trim()}…` : sentence;
}

export function LoginPage() {
  const { user, login } = useSession();
  const t = useAdminT();
  const { productName, loginSubtitle, logoUrl } = useUiPrefs();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const brandMark = productName.trim().charAt(0).toUpperCase() || 'U';

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    const code = await login(email, password);
    setError(code === 'INVALID_CREDENTIALS' ? t('auth.invalidCredentials') : code);
    setPending(false);
  }

  const mobileBrand = logoUrl ? (
    <img src={logoUrl} alt={productName} className="h-9 w-auto max-w-[12rem] object-contain" />
  ) : (
    <div className="flex items-center gap-3">
      <span
        className="grid size-9 place-items-center rounded-[var(--admin-radius-sm)] text-sm font-bold text-white"
        style={{ backgroundColor: 'var(--admin-accent, #2563eb)' }}
      >
        {brandMark}
      </span>
      <span className="admin-display text-base font-semibold text-slate-900 dark:text-slate-100">
        {productName}
      </span>
    </div>
  );

  return (
    <AuthFrame
      title={t('auth.login.title', { product: productName })}
      subtitle={shortLoginSubtitle(loginSubtitle)}
      brand={mobileBrand}
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4">
        <Field label={t('auth.email')}>
          <div className="relative mt-1.5">
            <Icon
              name="mail"
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400"
            />
            <TextInput
              type="email"
              autoComplete="username"
              inputMode="email"
              placeholder="name@company.gr"
              required
              autoFocus
              flush
              className="h-11 rounded-[var(--admin-radius-sm)] pr-4 pl-10"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </Field>
        <Field label={t('auth.password')}>
          <div className="relative mt-1.5">
            <Icon
              name="key"
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400"
            />
            <TextInput
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('auth.passwordPlaceholder')}
              required
              flush
              className="h-11 rounded-[var(--admin-radius-sm)] pr-12 pl-10"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((visible) => !visible)}
              className="absolute top-1/2 right-2 grid size-8 -translate-y-1/2 place-items-center rounded-[var(--admin-radius-sm)] text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--admin-accent,#2563eb)] dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <Icon name="eye" className="size-4" />
            </button>
          </div>
        </Field>
        <div className="-mt-1 flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-[color:var(--admin-accent,#2563EB)] transition hover:brightness-90 dark:hover:brightness-125"
          >
            {t('auth.forgot')}
          </Link>
        </div>
        {error ? <ErrorNote message={error} /> : null}
        <Button
          type="submit"
          disabled={pending}
          aria-busy={pending}
          className="h-11 w-full rounded-[var(--admin-radius-sm)] text-[15px]"
        >
          <Icon name="login" className="size-4" />
          {pending ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
        <div className="flex items-center justify-center gap-2 pt-1 text-xs text-slate-400 dark:text-slate-500">
          <Icon name="shield" className="size-3.5" />
          <span>{t('auth.secureAccess')}</span>
        </div>
      </form>
    </AuthFrame>
  );
}
